import { PageHero } from '@components/common';
import { getSectorCard } from '@data/sectorCards';
import { CALENDLY_URL } from '@constants';
import { Link } from 'react-router-dom';
import { Shield, AlertTriangle, Cloud, Lock, Rocket } from 'lucide-react';
import PageFAQ from '@components/sections/PageFAQ';

export default function SaasPage() {
  const sectorSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Fractional CISO for SaaS & Technology',
    description:
      'Cybersecurity leadership for scaling software companies navigating SOC 2, ISO 27001, and enterprise customer security requirements',
    url: 'https://thecisonetwork.com/sectors/saas',
    provider: {
      '@type': 'Organization',
      name: 'The CISO Network',
    },
    areaServed: {
      '@type': 'DefinedTerm',
      name: 'SaaS & Technology',
    },
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(sectorSchema)}</script>
      <PageHero
        title="Fractional CISO for SaaS & Technology"
        subtitle="Cybersecurity leadership for scaling software companies navigating SOC 2, ISO 27001, and enterprise customer security requirements"
        eyebrow="SaaS & Technology"
        backgroundImage={getSectorCard('saas')?.image}
        backgroundAlt={getSectorCard('saas')?.imageAlt}
        seoTitle="Fractional CISO SaaS & Technology | The CISO Network"
        seoDescription="Expert fractional CISO services for SaaS companies. Achieve SOC 2, ISO 27001, and meet enterprise customer security requirements without the full-time cost."
      />

      {/* Why SaaS Needs CISOs */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-6">
                Security Leadership for Scaling Software Companies
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  SaaS companies face a unique security challenge: you are building the product,
                  selling to enterprise customers, and handling sensitive customer data - often with
                  a small team and limited resources. Your customers increasingly demand proof of
                  security through{' '}
                  <Link to="/services/compliance" className="text-secondary hover:underline">
                    SOC 2, ISO 27001
                  </Link>
                  , or detailed security questionnaires. Without answers, deals stall or never
                  close.
                </p>
                <p>
                  The talent market makes hiring a full-time CISO prohibitive for most scaling SaaS
                  companies. A senior CISO in London costs £180,000+ plus equity, recruitment fees,
                  and months of searching. Meanwhile, your engineering team is shipping code daily,
                  your sales team is fielding security questions they cannot answer, and your CEO is
                  asking what happens if you get breached.
                </p>
                <p>
                  Our fractional CISOs give you senior security leadership on a schedule that fits
                  your stage. We help you achieve the certifications your customers demand, embed
                  security into your development process, and build the security culture that lets
                  you sell to enterprise with confidence.
                </p>
              </div>
            </div>
            <div className="space-y-6">
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Cloud className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Certification Readiness
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Navigate SOC 2, ISO 27001, and customer security assessments. Our CISOs have
                      guided dozens of SaaS companies through successful audits.
                    </p>
                  </div>
                </div>
              </div>
              <div className="p-6 rounded-xl bg-background border-l-4 border-teal">
                <div className="flex items-start gap-4">
                  <Rocket className="h-6 w-6 text-teal flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-heading font-semibold text-foreground mb-2">
                      Security at Speed
                    </h3>
                    <p className="text-muted-foreground text-sm">
                      Embed security into your development lifecycle without slowing down. We help
                      you build secure products that ship fast.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Historical Security Cases */}
      <section className="py-24 bg-background">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              When SaaS Security Fails
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              Historical cases where SaaS companies suffered breaches that a CISO could have
              prevented
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: AlertTriangle,
                title: 'Okta (2022)',
                impact: '366 customers compromised, significant remediation cost',
                description:
                  'A support engineer account was compromised, allowing attackers to access customer data. A CISO would have enforced MFA for all privileged accounts, implemented just-in-time access, and established vendor access monitoring - preventing the breach entirely.',
              },
              {
                icon: AlertTriangle,
                title: 'Slack (2015)',
                impact: 'User database compromised, hashed passwords accessed',
                description:
                  'Attackers accessed a central user database through a compromised employee account. A CISO would have implemented network segmentation, zero-trust architecture, and comprehensive access logging to prevent and detect such intrusions.',
              },
              {
                icon: AlertTriangle,
                title: 'CircleCI (2023)',
                impact: 'Source code and secrets compromised',
                description:
                  'Attackers gained access to production systems and exfiltrated customer data including environment variables, tokens, and keys. A CISO would implement secrets management, short-lived credentials, and rapid incident response capabilities.',
              },
              {
                icon: AlertTriangle,
                title: 'LastPass (2022)',
                impact: 'Password vaults compromised, source code stolen',
                description:
                  'A developer account breach led to source code theft and eventually customer password vaults being accessed. A CISO would enforce strict developer environment security, zero-trust access, and separate production from development systems.',
              },
              {
                icon: AlertTriangle,
                title: 'Twilio (2022)',
                impact: '209 customers compromised via phishing',
                description:
                  'Sophisticated SMS phishing attacks tricked employees into revealing credentials. A CISO would implement FIDO2 hardware keys, eliminate SMS-based MFA, and run regular phishing simulations to train staff.',
              },
              {
                icon: AlertTriangle,
                title: 'HubSpot (2022)',
                impact: 'Crypto customer data accessed',
                description:
                  'A compromised employee account led to unauthorised access to customer data at several cryptocurrency companies. A CISO would implement least-privilege access, behavioural analytics, and rapid account compromise detection.',
              },
            ].map((case_, i) => (
              <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-border">
                <div className="h-12 w-12 rounded-xl bg-red-100 flex items-center justify-center mb-4">
                  <case_.icon className="h-6 w-6 text-red-600" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {case_.title}
                </h3>
                <p className="text-sm font-medium text-red-600 mb-3">{case_.impact}</p>
                <p className="text-muted-foreground text-sm">{case_.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Risks */}
      <section className="py-24 bg-white">
        <div className="container-custom">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-foreground mb-4">
              Critical Risks Facing SaaS Companies
            </h2>
            <p className="text-muted-foreground max-w-3xl mx-auto">
              The threats that can derail your growth and destroy customer trust
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Shield,
                title: 'Supply Chain Attacks',
                description:
                  'Your dependencies are your weakness. Compromised libraries and third-party services can give attackers direct access to your code and data.',
              },
              {
                icon: Cloud,
                title: 'Cloud Misconfiguration',
                description:
                  'Exposed S3 buckets, open databases, and overly permissive IAM roles are found in almost every SaaS environment we assess.',
              },
              {
                icon: Lock,
                title: 'Insecure Development',
                description:
                  'Without security in your SDLC, vulnerabilities ship to production. Each release increases technical debt and risk.',
              },
              {
                icon: Rocket,
                title: 'Compliance Failures',
                description:
                  'Enterprise deals require SOC 2, ISO 27001, or detailed security assessments. Fail these and you fail to grow.',
              },
            ].map((risk, i) => (
              <div key={i} className="text-center">
                <div className="h-14 w-14 mx-auto rounded-xl bg-teal flex items-center justify-center mb-5">
                  <risk.icon className="h-7 w-7 text-accent-foreground" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-foreground mb-2">
                  {risk.title}
                </h3>
                <p className="text-muted-foreground text-sm">{risk.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <PageFAQ
        title="Fractional CISO for SaaS - FAQs"
        subtitle="Common questions from SaaS founders and platform businesses"
        items={[
          {
            id: 'saas-1',
            question: 'How does a fractional CISO work with SaaS companies?',
            answer:
              'Our CISOs embed with SaaS teams to build security into the product and business from the ground up. They work alongside engineering, product, and leadership teams — attending sprint planning, reviewing architecture, and ensuring security does not become a bottleneck to shipping.',
          },
          {
            id: 'saas-2',
            question: 'What compliance do SaaS companies need for enterprise customers?',
            answer:
              'Enterprise SaaS customers typically require SOC 2 Type II, ISO 27001, or both. Some sectors demand additional certifications. Our CISOs help SaaS companies achieve the specific certifications that unlock their target market and accelerate sales cycles.',
          },
          {
            id: 'saas-3',
            question: 'How quickly can a fractional CISO get us SOC 2 certified?',
            answer:
              'Most SaaS companies achieve SOC 2 Type I within 3-4 months and Type II within 8-10 months with our CISOs guiding the process. The timeline depends on your starting maturity, but our structured approach ensures you do not waste time on unnecessary controls.',
          },
          {
            id: 'saas-4',
            question: 'Can a fractional CISO help with enterprise security questionnaires?',
            answer:
              'Yes. Enterprise security questionnaires are a major sales bottleneck for SaaS companies. Our CISOs help you build a security documentation library, create standard responses, and establish processes that turn security reviews from a blocker into a competitive advantage.',
          },
          {
            id: 'saas-5',
            question: 'What is the biggest security mistake SaaS startups make?',
            answer:
              'Treating security as a feature to add later rather than a foundation to build on. The most successful SaaS companies embed security into their architecture, development processes, and culture from the start. Our CISOs help you build this foundation without slowing product velocity.',
          },
        ]}
      />

      {/* CTA */}
      <section className="py-24 gradient-navy cta-photo">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary-foreground mb-6">
            Secure Your SaaS Growth
          </h2>
          <p className="text-primary-foreground/60 text-lg max-w-3xl mx-auto mb-10">
            Do not let security become a blocker to your growth. Our fractional CISOs help you
            achieve compliance, secure your product, and close enterprise deals.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href={CALENDLY_URL} target="_blank" rel="noopener noreferrer" className="btn-hero">
              Book a Call
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
